import { useState, useEffect, useRef } from "react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { chatWithGemini, getGeminiApiKey } from "@/lib/gemini-service";
import { useAuth } from "@/hooks/useAuth";
import { useGeneration } from "@/hooks/useGeneration";
import { supabase } from "@/integrations/supabase/client";
import {
    MessageSquare,
    Sparkles,
    Bot,
    User,
    Loader2,
    Clock,
    Send,
    ChevronLeft,
    Terminal,
    Eraser,
    MessageCircle,
    Coins,
    Zap,
    Copy,
    Check,
    Mic,
    Image as ImageIcon,
    Video as VideoIcon,
    Info
} from "lucide-react";




interface PromptAiTemplate {
    id: string;
    title: string;
    title_th: string | null;
    description: string | null;
    description_th: string | null;
    prompt: string;
    credits_required: number;
    color_class: string;
    icon_name: string;
}

interface ChatMessage {
    id: string;
    role: "user" | "assistant";
    content: string;
    timestamp: Date;
}

interface ConversationHistory {
    id: string;
    title: string | null;
    created_at: string;
    template_id: string | null;
}

const JsonMessageView = ({ content, onCopy }: { content: string, onCopy: (text: string) => void }) => {
    try {
        // Try to extract JSON if it's wrapped in markdown code blocks
        let cleanContent = content;
        if (content.includes('```json')) {
            cleanContent = content.split('```json')[1].split('```')[0].trim();
        } else if (content.includes('```')) {
            cleanContent = content.split('```')[1].split('```')[0].trim();
        }

        const data = JSON.parse(cleanContent);


        return (
            <div className="space-y-6 w-full max-w-full overflow-hidden text-foreground">
                {/* Meta Section */}
                {data.meta && (
                    <div className="bg-primary/5 rounded-xl p-4 border border-primary/10">
                        <div className="flex items-center gap-2 mb-2 text-primary font-bold">
                            <Info className="h-4 w-4" />
                            <span>Metadata</span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-xs">
                            <div>
                                <span className="text-muted-foreground block">Food (TH/EN):</span>
                                <span className="font-medium">{data.meta.food_name_th} / {data.meta.food_name_en}</span>
                            </div>
                            <div>
                                <span className="text-muted-foreground block">Style:</span>
                                <span className="font-medium">{data.meta.content_style}</span>
                            </div>
                            <div className="col-span-2">
                                <span className="text-muted-foreground block">Role:</span>
                                <span className="font-medium">{data.meta.director_role}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Section 1: Transcription */}
                {data.section_1_transcription && (
                    <div className="space-y-3">
                        <div className="flex items-center justify-between text-orange-500 font-bold border-b pb-2">
                            <div className="flex items-center gap-2">
                                <Mic className="h-4 w-4" />
                                <span>Section 1: Transcription ({data.section_1_transcription.total_duration_seconds}s)</span>
                            </div>
                            {data.section_1_transcription.scenes?.length > 0 && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-[10px] gap-2 border-orange-500/20 text-orange-600 hover:bg-orange-500/5 transition-all"
                                    onClick={() => {
                                        const allDialogues = data.section_1_transcription.scenes
                                            .map((s: any) => s.dialogue_th)
                                            .join('\n\n');
                                        onCopy(allDialogues);
                                    }}
                                >
                                    <Copy className="h-3 w-3" />
                                    คัดลอกบทพูดทั้งหมด (Copy All Dialogues)
                                </Button>
                            )}
                        </div>

                        <div className="space-y-3">
                            {data.section_1_transcription.scenes?.map((scene: any, idx: number) => (
                                <div key={idx} className="bg-muted/40 rounded-lg p-3 text-xs border border-border/50 group relative">
                                    <div className="flex justify-between items-start mb-2 opacity-60">
                                        <span>Scene {idx + 1} • {scene.time}</span>
                                        <div className="flex items-center gap-2">
                                            <Badge variant="outline" className="text-[10px]">{scene.character}</Badge>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 text-muted-foreground hover:text-primary transition-colors"
                                                onClick={() => onCopy(scene.dialogue_th)}
                                                title="Copy Dialogue"
                                            >
                                                <Copy className="h-3 w-3" />
                                            </Button>
                                        </div>
                                    </div>
                                    <p className="mb-2 italic text-muted-foreground">{scene.action}</p>
                                    <p className="font-medium text-sm leading-relaxed">{scene.dialogue_th}</p>
                                </div>
                            ))}
                        </div>

                    </div>
                )}

                {/* Section 2: Tone of Voice */}
                {data.section_2_tone_of_voice && (
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 text-blue-500 font-bold border-b pb-2">
                            <MessageSquare className="h-4 w-4" />
                            <span>Section 2: Tone of Voice</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-xs">
                            <div className="bg-muted/30 p-3 rounded-lg">
                                <span className="text-muted-foreground block mb-1">Mood & Style:</span>
                                <div>{data.section_2_tone_of_voice.mood} • {data.section_2_tone_of_voice.style}</div>
                            </div>
                            <div className="bg-muted/30 p-3 rounded-lg">
                                <span className="text-muted-foreground block mb-1">Keywords:</span>
                                <div className="flex flex-wrap gap-1">
                                    {data.section_2_tone_of_voice.keywords?.map((k: string, i: number) => (
                                        <Badge key={i} variant="secondary" className="text-[10px]">{k}</Badge>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Section 3: AI Prompts */}
                {data.section_3_ai_prompts && (
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 text-purple-500 font-bold border-b pb-2">
                            <Sparkles className="h-4 w-4" />
                            <span>Section 3: AI Generation Prompts</span>
                        </div>

                        <div className="space-y-3">
                            {/* Image Prompts */}
                            {data.section_3_ai_prompts.image_prompts?.length > 0 && (
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
                                        <ImageIcon className="h-3 w-3" />
                                        <span>Image Prompts</span>
                                    </div>
                                    {data.section_3_ai_prompts.image_prompts.map((p: string, i: number) => (
                                        <div key={i} className="flex gap-2 group">
                                            <div className="bg-muted/50 rounded-lg p-3 text-xs flex-1 border border-border/30 group-hover:border-primary/30 transition-colors">
                                                {p}
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="shrink-0 h-8 w-8 hover:bg-primary/10 hover:text-primary"
                                                onClick={() => onCopy(p)}
                                            >
                                                <Copy className="h-3 w-3" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Video Prompts */}
                            {data.section_3_ai_prompts.video_prompts?.length > 0 && (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 border-b border-border/10 pb-2">
                                        <div className="flex items-center gap-2">
                                            <VideoIcon className="h-3 w-3" />
                                            <span>Video Prompts & Scripts (พร้อมใช้งาน)</span>
                                        </div>
                                        {/* Copy All Combined Button */}
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-7 text-[10px] gap-2 border-green-500/20 text-green-600 hover:bg-green-500/5"
                                            onClick={() => {
                                                const allCombined = data.section_3_ai_prompts.video_prompts
                                                    .map((p: any, idx: number) => {
                                                        const promptText = typeof p === 'string' ? p : (p.prompt || '');
                                                        const scriptText = typeof p === 'object' ? (p.script || p.dialogue || '') : '';
                                                        return `=== Scene ${idx + 1} ===\n[AI Prompt]\n${promptText}${scriptText ? `\n\n[คำพูด/Dialogue]\n${scriptText}` : ''}`;
                                                    })
                                                    .join('\n\n');
                                                onCopy(allCombined);
                                            }}
                                        >
                                            <Copy className="h-3 w-3" />
                                            คัดลอกทั้งหมด (Copy All Prompts + Scripts)
                                        </Button>
                                    </div>

                                    {data.section_3_ai_prompts.video_prompts.map((p: any, i: number) => {
                                        const promptText = typeof p === 'string' ? p : (p.prompt || '');
                                        const scriptText = typeof p === 'object' ? (p.script || p.dialogue || '') : '';
                                        const combinedText = `${promptText}${scriptText ? `\n\n[คำพูด]: ${scriptText}` : ''}`;

                                        return (
                                            <div key={i} className="space-y-2 pb-4 border-b border-border/20 last:border-0">
                                                {/* Combined View - Prompt + Script together */}
                                                <div className="flex gap-2 group">
                                                    <div className="bg-gradient-to-br from-muted/60 to-orange-500/5 rounded-lg p-4 text-xs flex-1 border border-border/30 group-hover:border-primary/30 transition-colors space-y-3">
                                                        {/* Prompt Section */}
                                                        <div>
                                                            <span className="text-[10px] font-bold text-primary block mb-1 uppercase tracking-tighter">🎬 AI Video Prompt {i + 1}</span>
                                                            <p className="text-foreground">{promptText}</p>
                                                        </div>

                                                        {/* Script/Dialogue Section */}
                                                        {scriptText && (
                                                            <div className="border-t border-orange-500/20 pt-3">
                                                                <div className="flex items-center gap-1.5 text-[10px] font-bold text-orange-500 mb-1 uppercase tracking-tighter">
                                                                    <Mic className="h-2.5 w-2.5" />
                                                                    <span>🎤 คำสั่งพูด / Dialogue</span>
                                                                </div>
                                                                <p className="italic text-orange-600 dark:text-orange-400 font-medium leading-relaxed">{scriptText}</p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Copy Combined Button */}
                                                    <div className="flex flex-col gap-1">
                                                        <Button
                                                            variant="outline"
                                                            size="icon"
                                                            className="shrink-0 h-10 w-10 hover:bg-green-500/10 hover:text-green-500 hover:border-green-500/30"
                                                            onClick={() => onCopy(combinedText)}
                                                            title="Copy Prompt + Script (All)"
                                                        >
                                                            <Copy className="h-4 w-4" />
                                                        </Button>
                                                        {scriptText && (
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="shrink-0 h-8 w-8 hover:bg-orange-500/10 hover:text-orange-500"
                                                                onClick={() => onCopy(scriptText)}
                                                                title="Copy Script Only"
                                                            >
                                                                <Mic className="h-3 w-3" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                        </div>

                    </div>
                )}

                {/* Section 4: Full Monologue */}
                {data.section_4_full_monologue && (
                    <div className="space-y-3">
                        <div className="flex items-center justify-between border-b pb-2">
                            <div className="flex items-center gap-2 text-green-500 font-bold">
                                <Terminal className="h-4 w-4" />
                                <span>Section 4: Full Monologue ({data.section_4_full_monologue.style})</span>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-[10px] gap-1"
                                onClick={() => onCopy(data.section_4_full_monologue.script_th)}
                            >
                                <Copy className="h-3 w-3" />
                                Copy Script
                            </Button>
                        </div>
                        <div className="bg-muted/50 rounded-xl p-4 text-sm leading-relaxed whitespace-pre-wrap border border-border/50 font-body italic">
                            {data.section_4_full_monologue.script_th}
                        </div>
                    </div>
                )}
            </div>
        );
    } catch (e) {
        return <p className="whitespace-pre-wrap text-sm leading-relaxed">{content}</p>;
    }
};

const PromptAI = () => {

    const { toast } = useToast();
    const { t, language } = useLanguage();
    const { user, profile, refreshProfile } = useAuth();
    const { userCredits, deductCredits, updateLogStatus, refundCredits } = useGeneration();

    const [templates, setTemplates] = useState<PromptAiTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedTemplate, setSelectedTemplate] = useState<PromptAiTemplate | null>(null);
    const [view, setView] = useState<"grid" | "chat">("grid");

    // Chat state
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [chatInput, setChatInput] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [conversationId, setConversationId] = useState<string | null>(null);

    // History state
    const [history, setHistory] = useState<ConversationHistory[]>([]);
    const [historyPage, setHistoryPage] = useState(1);
    const [totalHistory, setTotalHistory] = useState(0);
    const itemsPerPage = 15;

    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        fetchTemplates();
        if (user?.id) {
            fetchHistory();
        }
    }, [user]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [chatMessages]);

    const fetchTemplates = async () => {
        try {
            const { data, error } = await supabase
                .from("prompt_ai_templates" as any)
                .select("*")
                .eq("is_active", true)
                .order("sort_order", { ascending: true });

            if (error) throw error;
            setTemplates((data || []) as any);
        } catch (error) {
            console.error("Error fetching templates:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = async () => {
        if (!user?.id) return;
        try {
            const { data, error, count } = await supabase
                .from("chat_conversations" as any)
                .select("*", { count: "exact" })
                .eq("user_id", user.id)
                .not("template_id", "is", null)
                .order("created_at", { ascending: false })
                .range((historyPage - 1) * itemsPerPage, historyPage * itemsPerPage - 1);

            if (error) throw error;
            setHistory((data || []) as any);
            setTotalHistory(count || 0);
        } catch (error) {
            console.error("Error fetching history:", error);
        }
    };

    const handleSelectTemplate = (template: PromptAiTemplate) => {
        setSelectedTemplate(template);
        setChatMessages([
            {
                id: "welcome",
                role: "assistant",
                content: language === "th"
                    ? `สวัสดีครับ! ผมเป็นผู้เชี่ยวชาญด้าน ${template.title_th || template.title} \n\nบอกหัวข้อที่ต้องการให้ผมช่วยสร้างได้เลยครับ (เช่น ผลไม้, หัวใจ, ตัวการ์ตูน)`
                    : `Hello! I'm an expert in ${template.title}. \n\nPlease tell me the topic you want me to help create (e.g., fruit, heart, cartoon character).`,
                timestamp: new Date()
            }
        ]);
        setConversationId(null);
        setView("chat");
    };

    const fetchMessages = async (convId: string) => {
        try {
            const { data, error } = await supabase
                .from("chat_messages" as any)
                .select("*")
                .eq("conversation_id", convId)
                .order("created_at", { ascending: true });

            if (error) throw error;
            if (data) {
                setChatMessages(data.map((m: any) => ({
                    id: m.id,
                    role: m.role,
                    content: m.content,
                    timestamp: new Date(m.created_at)
                })));
            }
        } catch (error) {
            console.error("Error fetching messages:", error);
        }
    };

    const handleSelectHistory = async (conv: ConversationHistory) => {
        const template = templates.find(t => t.id === conv.template_id);
        if (template) {
            setSelectedTemplate(template);
            setConversationId(conv.id);
            setView("chat");
            await fetchMessages(conv.id);
        }
    };

    const handleSendMessage = async () => {
        if (!chatInput.trim() || !user?.id || !selectedTemplate || isSending) return;

        const creditsNeeded = selectedTemplate.credits_required || 4;

        if (userCredits < creditsNeeded) {
            toast({
                title: t("เครดิตไม่เพียงพอ", "Insufficient credits"),
                description: t(`ต้องการ ${creditsNeeded} เครดิตต่อข้อความ`, `Requires ${creditsNeeded} credits per message`),
                variant: "destructive"
            });
            return;
        }

        const userMessage: ChatMessage = {
            id: Date.now().toString(),
            role: "user",
            content: chatInput,
            timestamp: new Date()
        };

        setChatMessages(prev => [...prev, userMessage]);
        const currentInput = chatInput;
        setChatInput("");
        setIsSending(true);

        let logId: string | undefined;

        try {
            // 1. Get or Create conversation
            let activeId = conversationId;
            if (!activeId) {
                const { data: newConv, error: convError } = await supabase
                    .from("chat_conversations" as any)
                    .insert({
                        user_id: user.id,
                        title: `${selectedTemplate.title}: ${currentInput.substring(0, 20)}...`,
                        template_id: selectedTemplate.id
                    })
                    .select("id")
                    .single();

                if (convError) throw convError;
                activeId = (newConv as any).id;

                setConversationId(activeId);
                fetchHistory(); // Refresh history list
            }

            // 2. Deduct Credits
            const deduction = await deductCredits(creditsNeeded, "prompt_ai" as any, "prompt-ai", currentInput);
            if (!deduction.success) {
                setChatInput(currentInput);
                setIsSending(false);
                return;
            }
            logId = deduction.logId;

            // 3. Log user message
            await supabase.from("chat_messages" as any).insert({
                conversation_id: activeId,
                user_id: user.id,
                role: "user",
                content: currentInput,
                credits_used: creditsNeeded
            });

            // 4. Call Gemini
            const apiKey = await getGeminiApiKey();

            // Use systemInstruction for the template prompt
            const fullMessages = [
                ...chatMessages.map(m => ({ role: m.role, content: m.content })),
                { role: "user", content: currentInput }
            ];

            const responseText = await chatWithGemini(fullMessages as any, apiKey, selectedTemplate.prompt);


            // 5. Log assistant message
            await supabase.from("chat_messages" as any).insert({
                conversation_id: activeId,
                user_id: user.id,
                role: "assistant",
                content: responseText,
                credits_used: 0
            });

            if (logId) {
                await updateLogStatus(logId, "completed", "prompt-ai-response");
            }

            const aiResponse: ChatMessage = {
                id: Date.now().toString() + "-ai",
                role: "assistant",
                content: responseText,
                timestamp: new Date()
            };

            setChatMessages(prev => [...prev, aiResponse]);
            await refreshProfile();

            toast({
                title: t(`หัก ${creditsNeeded} เครดิต`, `Deducted ${creditsNeeded} credits`),
                description: t("สร้างคำตอบสำเร็จ", "Response generated successfully")
            });

        } catch (error: any) {
            console.error("Chat Error:", error);
            if (logId) {
                await refundCredits(logId);
            }
            toast({
                title: t("เกิดข้อผิดพลาด", "Error occurred"),
                description: error.message || t("ไม่สามารถสร้างคำตอบได้", "Failed to generate response"),
                variant: "destructive"
            });
            setChatInput(currentInput);
        } finally {
            setIsSending(false);
        }
    };

    const getIcon = (name: string) => {
        switch (name) {
            case 'sparkles': return Sparkles;
            case 'bot': return Bot;
            case 'message': return MessageSquare;
            case 'zap': return Zap;
            case 'type': return Terminal;
            case 'layout': return MessageCircle;
            default: return Bot;
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <Navbar />

            <main className="container mx-auto px-4 pt-24 pb-16">
                {/* Header */}
                {view === "grid" && (
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-4">
                            <Bot className="h-4 w-4" />
                            <span className="text-sm font-medium">Prompt AI Assistant</span>
                        </div>
                        <h1 className="text-4xl font-heading font-bold mb-4">
                            <span className="text-gradient">Prompt AI</span> - {t("ระบบผู้ช่วยอัจฉริยะ", "Intelligent AI Assistant")}
                        </h1>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            {t("เลือกผู้ช่วยเฉพาะทางที่ต้องการ เพื่อให้ AI ช่วยเตรียม Prompt และความรู้สำหรับคุณโดยเฉพาะ", "Select a specialized assistant to help you prepare prompts and knowledge specifically for you.")}
                        </p>
                        <div className="flex items-center justify-center gap-2 mt-4">
                            <Coins className="h-5 w-5 text-primary" />
                            <span className="font-semibold">{(userCredits || 0).toLocaleString()} {t("เครดิต", "Credits")}</span>
                        </div>
                    </div>
                )}

                {view === "grid" ? (
                    <div className="space-y-12">
                        {/* Grid View */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {templates.map((template) => {
                                const IconComp = getIcon(template.icon_name);
                                return (
                                    <Card
                                        key={template.id}
                                        className="group cursor-pointer border-border/50 hover:border-primary/50 transition-all hover:shadow-2xl hover:shadow-primary/5 overflow-hidden"
                                        onClick={() => handleSelectTemplate(template)}
                                    >
                                        <div className={`h-32 bg-gradient-to-br ${template.color_class} flex items-center justify-center relative`}>
                                            <IconComp className="h-12 w-12 text-white/50 group-hover:scale-110 transition-transform" />
                                            <Badge variant="secondary" className="absolute top-3 right-3 bg-black/20 text-white backdrop-blur-md border-white/10">
                                                {template.credits_required} Credits
                                            </Badge>
                                        </div>
                                        <CardHeader>
                                            <CardTitle className="group-hover:text-primary transition-colors">
                                                {language === "th" ? template.title_th || template.title : template.title}
                                            </CardTitle>
                                            <CardDescription className="line-clamp-2">
                                                {language === "th" ? template.description_th || template.description : template.description}
                                            </CardDescription>
                                        </CardHeader>
                                    </Card>
                                );
                            })}
                        </div>

                        {/* History Section Below Grid */}
                        <div className="max-w-4xl mx-auto">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold flex items-center gap-2">
                                    <Clock className="h-6 w-6 text-primary" />
                                    {t("ประวัติการใช้งาน", "Prompt History")}
                                </h2>
                                <Badge variant="outline">{totalHistory} {t("รายการ", "Sessions")}</Badge>
                            </div>

                            <div className="grid gap-3">
                                {history.map((conv) => {
                                    const template = templates.find(t => t.id === conv.template_id);
                                    return (
                                        <Card
                                            key={conv.id}
                                            className="p-4 cursor-pointer hover:bg-muted/50 transition-colors flex items-center justify-between group"
                                            onClick={() => handleSelectHistory(conv)}
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${template?.color_class || 'from-muted to-muted'} flex items-center justify-center shrink-0`}>
                                                    <Bot className="h-5 w-5 text-white/60" />
                                                </div>
                                                <div>
                                                    <p className="font-medium group-hover:text-primary transition-colors line-clamp-1">{conv.title || "Untitled Chat"}</p>
                                                    <p className="text-xs text-muted-foreground">{new Date(conv.created_at).toLocaleDateString("th-TH")}</p>
                                                </div>
                                            </div>
                                            <Badge variant="outline" className="text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">
                                                View Chat
                                            </Badge>
                                        </Card>
                                    );
                                })}

                                {history.length === 0 && !loading && (
                                    <div className="text-center py-20 bg-muted/20 rounded-xl border-2 border-dashed">
                                        <Eraser className="h-10 w-10 mx-auto text-muted-foreground opacity-20 mb-2" />
                                        <p className="text-muted-foreground">{t("ไม่มีประวัติการใช้งาน", "No history found")}</p>
                                    </div>
                                )}
                            </div>

                            {/* Pagination (Simplified) */}
                            {totalHistory > itemsPerPage && (
                                <div className="flex items-center justify-center gap-4 mt-8">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={historyPage === 1}
                                        onClick={() => { setHistoryPage(p => p - 1); fetchHistory(); }}
                                    >
                                        Previous
                                    </Button>
                                    <span className="text-sm font-medium">Page {historyPage} of {Math.ceil(totalHistory / itemsPerPage)}</span>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={historyPage >= Math.ceil(totalHistory / itemsPerPage)}
                                        onClick={() => { setHistoryPage(p => p + 1); fetchHistory(); }}
                                    >
                                        Next
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    /* Chat View */
                    <div className="max-w-4xl mx-auto">
                        <Button
                            variant="ghost"
                            className="mb-4 gap-2 hover:bg-transparent hover:text-primary px-0"
                            onClick={() => setView("grid")}
                        >
                            <ChevronLeft className="h-4 w-4" />
                            {t("กลับหน้าหลัก", "Back to Hub")}
                        </Button>

                        <Card variant="glass" className="overflow-hidden flex flex-col h-[700px] border-primary/20">
                            <CardHeader className="border-b border-border/50 bg-muted/30 backdrop-blur-md">
                                <div className="flex items-center gap-4">
                                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${selectedTemplate?.color_class} flex items-center justify-center shadow-lg shadow-primary/20`}>
                                        {selectedTemplate && (
                                            (() => {
                                                const Icon = getIcon(selectedTemplate.icon_name);
                                                return <Icon className="h-6 w-6 text-white" />;
                                            })()
                                        )}
                                    </div>

                                    <div>
                                        <CardTitle className="text-xl">
                                            {selectedTemplate ? (language === "th" ? selectedTemplate.title_th : selectedTemplate.title) : "Assistant"}
                                        </CardTitle>
                                        <CardDescription className="flex items-center gap-2">
                                            <Bot className="h-3 w-3" />
                                            {t("กำลังสนทนากับ AI พิเศษ", "Chatting with Specialized AI")}
                                        </CardDescription>
                                    </div>
                                </div>
                            </CardHeader>

                            {/* Chat Area */}
                            <div
                                ref={scrollRef}
                                className="flex-1 overflow-y-auto p-6 space-y-6 scroll-smooth"
                            >
                                {chatMessages.map((msg) => (
                                    <div
                                        key={msg.id}
                                        className={`flex gap-4 ${msg.role === "user" ? "flex-row-reverse" : ""}`}
                                    >
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 shadow-sm ${msg.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted border border-border"
                                            }`}>
                                            {msg.role === "user" ? <User className="h-5 w-5" /> : <Bot className="h-5 w-5" />}
                                        </div>
                                        <div className={`max-w-[75%] rounded-2xl px-5 py-3 shadow-sm ${msg.role === "user"
                                            ? "bg-primary text-primary-foreground rounded-tr-sm"
                                            : "bg-muted text-foreground rounded-tl-sm border border-border/50 min-w-[300px]"
                                            }`}>
                                            {msg.role === "assistant" && msg.content.includes('{') && msg.content.includes('}') ? (
                                                <JsonMessageView
                                                    content={msg.content}
                                                    onCopy={(text) => {
                                                        navigator.clipboard.writeText(text);
                                                        toast({
                                                            title: t("คัดลอกแล้ว", "Copied to clipboard"),
                                                            duration: 2000
                                                        });
                                                    }}
                                                />
                                            ) : (
                                                <p className="whitespace-pre-wrap text-sm leading-relaxed">{msg.content}</p>
                                            )}
                                            <span className="text-[10px] opacity-40 mt-2 block text-right italic">
                                                {msg.timestamp.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" })}
                                            </span>
                                        </div>

                                    </div>
                                ))}
                                {isSending && (
                                    <div className="flex gap-4">
                                        <div className="w-10 h-10 rounded-full bg-muted border border-border flex items-center justify-center">
                                            <Bot className="h-5 w-5" />
                                        </div>
                                        <div className="bg-muted rounded-2xl px-6 py-3 border border-border/50">
                                            <Loader2 className="h-5 w-5 animate-spin text-primary" />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Input Area */}
                            <div className="p-6 border-t border-border/50 bg-card/50">
                                <div className="flex gap-3 relative">
                                    <Input
                                        placeholder={t(`บอกหัวข้อที่ต้องการ... (${selectedTemplate?.credits_required || 4} เครดิต)`, `Enter topic... (${selectedTemplate?.credits_required || 4} credits)`)}
                                        value={chatInput}
                                        onChange={(e) => setChatInput(e.target.value)}
                                        onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSendMessage()}
                                        className="h-12 bg-background border-border/50 focus-visible:ring-primary shadow-inner"
                                        disabled={isSending}
                                    />
                                    <Button
                                        onClick={handleSendMessage}
                                        disabled={isSending || !chatInput.trim()}
                                        className="h-12 px-6"
                                        variant="glow"
                                    >
                                        {isSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-5 w-5" />}
                                    </Button>
                                </div>
                                <p className="text-[10px] text-center text-muted-foreground mt-3 uppercase tracking-widest opacity-50">
                                    {t("AI อาจให้คำตอบที่ไม่ถูกต้อง โปรดตรวจสอบข้อมูลสำคัญเสมอ", "AI may provide inaccurate info. Verification recommended.")}
                                </p>
                            </div>
                        </Card>
                    </div>
                )}
            </main>

            <Footer />
        </div>
    );
};

export default PromptAI;
